import type { Composer } from 'vue-i18n';

// Shared loader for the locale files (packages/shared/src/locales/<core|feature>/<locale>.json). It owns
// the desired locale and the features requested so far, and loads core plus those features in the
// desired locale, with en-US as the fallback. Callers only say what changed: a route asks for its
// features, a language switch asks for a locale. The apps import core en-US directly, so it has no
// loader here.

export const FALLBACK_LOCALE = 'en-US';

type LocaleMessages = Record<string, unknown>;
type Loader = () => Promise<{ default: LocaleMessages }>;
type ComposerLike = Pick<Composer, 'mergeLocaleMessage'>;

const CORE = 'core';
const LOCALE_FILE = /\/locales\/([^/]+)\/([^/]+)\.json$/;

// '../locales/approvals/de-DE.json' -> index.approvals['de-DE'], '../locales/core/de-DE.json' -> index.core['de-DE'].
export const indexLocaleSetLoaders = (modules: Record<string, Loader>) => {
  const index: Record<string, Record<string, Loader>> = {};

  for (const [path, load] of Object.entries(modules)) {
    const match = path.match(LOCALE_FILE);
    if (match) {
      const [, set, locale] = match;
      (index[set] ??= {})[locale] = load;
    }
  }

  return index;
};

// A locale set is core or one feature. `modules` is each app's own `import.meta.glob` of the locale files
// it uses, passed in rather than globbed here: the glob is resolved at build time where it appears, so
// keeping it in the app stops a consumer (the MFE) from emitting chunks for features it never loads.
export const createLocaleSets = (composer: ComposerLike, modules: Record<string, Loader>) => {
  const loaders = indexLocaleSetLoaders(modules);
  const supportedLocales = [...new Set([FALLBACK_LOCALE, ...Object.keys(loaders[CORE] ?? {})])];
  const requiredFeatures = new Set<string>();
  let desiredLocale = FALLBACK_LOCALE;
  const inFlight = new Map<string, Promise<void>>();

  const loadOne = (set: string, locale: string) => {
    const key = `${set}:${locale}`;
    const existing = inFlight.get(key);
    if (existing) {
      return existing;
    }

    const load = loaders[set]?.[locale];
    if (!load) {
      return Promise.resolve();
    }

    // Forget a failed load so the next navigation retries it instead of reusing the rejection.
    const promise = load()
      .then(({ default: messages }) => composer.mergeLocaleMessage(locale, messages))
      .catch((error: unknown) => {
        inFlight.delete(key);
        throw error;
      });
    inFlight.set(key, promise);
    return promise;
  };

  const loadSets = async (features: Iterable<string>, locale: string) => {
    const targets = locale === FALLBACK_LOCALE ? [FALLBACK_LOCALE] : [FALLBACK_LOCALE, locale];

    await Promise.all([CORE, ...features].flatMap((set) => targets.map((target) => loadOne(set, target))));
  };

  // A feature without an en-US file is missing from the app's glob; a missing regional file falls back.
  const assertFeaturesExist = (features: string[]) => {
    const unknown = features.filter((feature) => !loaders[feature]?.[FALLBACK_LOCALE]);
    if (unknown.length > 0) {
      throw new Error(`No ${FALLBACK_LOCALE} locale file for feature(s): ${unknown.join(', ')}`);
    }
  };

  // Load the features in the desired locale, and in every locale prepared from now on.
  const ensureFeatures = async (features: string[]) => {
    assertFeaturesExist(features);
    features.forEach((feature) => requiredFeatures.add(feature));
    await loadSets(features, desiredLocale);
  };

  // Make `locale` the desired locale and load core plus every requested feature in it. Resolves to
  // whether it is still the desired locale, so a caller applies only the latest of overlapping switches.
  const prepareLocale = async (locale: string) => {
    desiredLocale = locale;
    await loadSets(requiredFeatures, locale);
    return desiredLocale === locale;
  };

  return { ensureFeatures, prepareLocale, supportedLocales };
};
